import { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { trackInternalEvent } from '@rio/tracking'
import { useParams } from 'react-router-dom'

import { Optional } from 'packages/types'
import { CoverLetterEditor } from 'builder/components/DocumentEditor'
import PreviewPanel, { useDocumentDownload } from 'builder/components/PreviewPanel'
import { DocumentCustomizationModalSwitcher } from 'builder/components/DocumentCustomizationModal'

import { actions, selectors } from 'builder/modules/coverLetterEditor'
import { EditorPanelType, actions as generalEditorActions } from 'builder/modules/generalEditor'
import { CoverLetterRegenerationStatus, DocumentTypes } from 'builder/modules/constants'
import { navigate } from 'builder/modules/navigate'
import { actions as uiActions } from 'builder/modules/ui'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import PreviewReloadService from 'builder/services/PreviewReloadService'
import { AFTER_USE_EXAMPLE_HIGHLIGHT_ID } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { EditorPanelSwitcherItem } from 'builder/components/EditorPanelSwitcher'
import { Container } from './styles'

export const CoverLetterEditorView = () => {
  const editorState = useTypedSelector(state => state.generalEditor)
  const coverLetterState = useTypedSelector(state => state.coverLetterEditor)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const isContentAnimationPending = useTypedSelector(selectors.isContentAnimationPending)
  const fallbackLocale = useTypedSelector(
    state => state.application.configs.country?.locale || 'en',
  )
  const isAPremiumUser = useTypedSelector(state => state.user.data?.hasPremiumFeatures || false)
  const aiClFreeAttempts = useTypedSelector(state => state.user.data?.aiClFreeAttempts || 0)
  const isDownloading = useTypedSelector(state => state.rendering.isDocumentDownloading)
  const params = useParams()
  const mediaQueries = useMediaQueries()
  const dispatch = useDispatch()
  const { i18n } = useI18n()

  const { coverLetter, openEditorEventTracked, error, isSyncing, editorPanelType } =
    coverLetterState
  const isFullScreen = editorPanelType === 'customization'

  const coverLetterId = parseInt(params.id || '')
  const currentDocument = !coverLetter
    ? null
    : isContentAnimationPending
    ? { ...coverLetter, content: '' }
    : coverLetter
  const { handleDownloadDocument } = useDocumentDownload({
    currentDocument,
    downloadClickEventSource: isFullScreen
      ? `full_screen_cover_letter_preview`
      : `cover_letter_editor`,
    downloadActionEventSource: isFullScreen ? 'builder_full_screen_preview' : 'builder',
  })
  const hasContent = !!currentDocument?.content

  const mobilePanelSwitcherItems: EditorPanelSwitcherItem<EditorPanelType>[] = [
    { text: i18n.t('builder.resume_editor.edit'), id: 'editor' },
    {
      text: i18n.t('builder.resume_editor.preview'),
      id: 'customization',
      dataTestId: AFTER_USE_EXAMPLE_HIGHLIGHT_ID,
    },
  ]

  useEffectOnMount(() => {
    dispatch(actions.fetchCoverLetterRequest({ id: coverLetterId }))
    dispatch(generalEditorActions.fetchEditorPayloadRequest())
  })

  useEffect(() => {
    if (!hasContent) return
    PreviewReloadService.reloadResume(currentDocument, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContent])

  useEffect(() => {
    if (error) navigate('/cover-letters')

    if (coverLetter && !openEditorEventTracked) {
      // track the page opening
      trackInternalEvent('visit_cover_letter_editor', {
        cover_letter_id: coverLetterId,
        ...(coverLetter.aiGenerated ? { label: 'ai_generated' } : {}),
      })
      dispatch(actions.setOpenEditorEventTracked(true))
    }
  }, [dispatch, coverLetter, coverLetterId, error, openEditorEventTracked])

  const handleFullSizeOpen = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      dispatch(actions.setEditorPanelType('customization'))
    },
    [dispatch],
  )

  const handleFullSizeClose = useCallback(() => {
    dispatch(actions.setEditorPanelType('editor'))
  }, [dispatch])

  const handleSimpleFieldUpdate = useCallback(
    (name: any, value: any, debounce?: boolean | number) => {
      dispatch(actions.updateSimpleField({ name, value, debounce }))

      if (name === 'name') trackInternalEvent('rename_cover_letter')
      if (name === 'locale') trackInternalEvent('change_cover_letter_locale', { locale: value })
      if (name === 'template')
        trackInternalEvent('change_cover_letter_template', { template: value })
    },
    [dispatch],
  )

  const handleTemplateUpdate = useCallback(
    (id: string) => {
      handleSimpleFieldUpdate('template', id)
    },
    [handleSimpleFieldUpdate],
  )

  const resetAIRegenerationStatus = useCallback(() => {
    dispatch(actions.setAIRegenerationStatus(CoverLetterRegenerationStatus.notStarted))
  }, [dispatch])

  const setShowConfirmRegenerateModal = useCallback(
    (value: boolean) => {
      dispatch(actions.setShowConfirmRegenerateModal(value))
    },
    [dispatch],
  )

  const setShowAICoverLetterPaywall = useCallback(() => {
    dispatch(actions.setShowAICoverLetterPaywall())
  }, [dispatch])

  const handleColorSelect = useCallback(
    (color: Optional<string>) => {
      dispatch(
        actions.updateSimpleField({
          name: 'color',
          value: color,
          debounce: false,
        }),
      )
    },
    [dispatch],
  )

  const handleCombineDocuments = useCallback(() => {
    dispatch(uiActions.openMergeModal({ resumeId: null, coverLetterId: coverLetter?.id }))
    trackInternalEvent('open_documents_merging_modal')
  }, [dispatch, coverLetter?.id])

  const handleDownloadButtonMenuOpen = useCallback(() => {
    trackInternalEvent('click_download_cover_letter_formats', {
      source: isFullScreen ? 'full_screen_cover_letter_preview' : 'cover_letter_editor',
      template: currentDocument?.template,
    })
  }, [currentDocument?.template, isFullScreen])

  const handleEditorPanelTypeChange = useCallback(
    (type: EditorPanelType) => {
      dispatch(actions.setEditorPanelType(type))
    },
    [dispatch],
  )

  return (
    <Container data-testid="cover-letter-editor-view">
      <CoverLetterEditor
        {...{
          editorState,
          coverLetterState,
          aiClFreeAttempts,
          isAPremiumUser,
          isContentAnimationPending,
          setShowAICoverLetterPaywall,
          fallbackLocale,
          setShowConfirmRegenerateModal,
          coverLetterId,
          resetAIRegenerationStatus,
          isOnline,
          mobilePanelSwitcherItems,
          handleDownloadDocument,
          handleCombineDocuments,
        }}
        updateSimpleField={handleSimpleFieldUpdate}
      />

      {/* Prevent rendering if the block isn't visible (reduce the load of user's device) */}
      {!isFullScreen && mediaQueries.isDesktop && currentDocument && (
        <PreviewPanel
          onFullSize={handleFullSizeOpen}
          type={DocumentTypes.coverLetter}
          documentId={coverLetterId}
          isSyncing={isSyncing}
          isDownloading={isDownloading}
          currentDocument={currentDocument}
          isFullScreen={isFullScreen}
          isOnline={isOnline}
          handleDownloadDocument={handleDownloadDocument}
          handleCombineDocuments={handleCombineDocuments}
          handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
        />
      )}

      {isFullScreen && currentDocument && (
        <DocumentCustomizationModalSwitcher
          type={DocumentTypes.coverLetter}
          isSyncing={isSyncing}
          isDownloading={isDownloading}
          currentDocument={currentDocument}
          editorState={editorState}
          onClose={handleFullSizeClose}
          handleDownloadDocument={handleDownloadDocument}
          updateTemplate={handleTemplateUpdate}
          handleColorSelect={handleColorSelect}
          handleCombineDocuments={handleCombineDocuments}
          handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
          editorPanelType={editorPanelType}
          mobilePanelSwitcherItems={mobilePanelSwitcherItems}
          handleEditorPanelTypeChange={handleEditorPanelTypeChange}
        />
      )}
    </Container>
  )
}
